import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { Observable } from 'rxjs';
import { DryEyeMedicalHistoryFormGroupValue } from '../dry-eye-medical-history-form.model';
import { MedicalHistoryRiskSummaryWithDetail } from '../medical-history-risk-factor.schema';

export interface MedicalHistorySegment {
  key: 'form' | 'summary';
  icon: string;
  label: string;
  hasLiveDot: boolean;
}

// Shared scaffold that wraps the medical-history form with the AI Summary UI
// (Form / AI Summary segment toggle, content card, regenerate header, quick-
// scan view). The form itself is content-projected so each caller keeps
// ownership of its own form bindings; this component only owns the chrome
// that is otherwise copy-pasted between the standard and DEWSIII sections.
@Component({
  selector: 'csi-medical-history-ai-section',
  templateUrl: './medical-history-ai-section.component.html',
  styleUrls: ['./medical-history-ai-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MedicalHistoryAiSectionComponent {
  @Input() isAiMedicalHistoryEnabled: boolean = false;
  @Input() medicalHistorySummaryGenerated: boolean = false;
  @Input() showMedicalHistorySummary: boolean = false;
  @Input() medicalHistorySegments: ReadonlyArray<MedicalHistorySegment> = [];
  @Input() medicalHistorySummary$: Observable<MedicalHistoryRiskSummaryWithDetail>;
  @Input() medicalHistoryFormValue$: Observable<DryEyeMedicalHistoryFormGroupValue>;
  @Input() medicalHistoryRevealTrigger$: Observable<void>;
  // CONTRACT: pass a ReplaySubject(1) so a late-mounting quick-scan child
  // (e.g. DEWSIII section, whose template branch resolves after a setTimeout)
  // still receives the buffered restore signal. See the @Input on
  // MedicalHistoryQuickScanComponent for the full rationale.
  @Input() medicalHistoryRestoreTrigger$: Observable<void>;

  // Two-way binding via [(showMedicalHistorySummary)] — emits when the user
  // toggles between Form and AI Summary via the segment toggle.
  @Output() showMedicalHistorySummaryChange = new EventEmitter<boolean>();
  @Output() regenerateClicked = new EventEmitter<void>();

  onSegmentToggleChange(value: string): void {
    this.showMedicalHistorySummaryChange.emit(value === 'summary');
  }

  onRegenerateClick(): void {
    this.regenerateClicked.emit();
  }
}
